import * as React from 'react'
import i18next from 'i18next'
import { FlatMonth, TimelineIndex } from '../GalleryView/timelineIndex'
import { computeYearMarks, shouldShowScrubber, YearMark } from './scrubberMath'

export interface UseDateScrubberOptions {
  timeline: TimelineIndex
  /** current scroll position of the gallery, 0..1 */
  fraction: number
  onJump: (month: FlatMonth) => void
  /** live-jump during drag is allowed only for already-loaded targets */
  isIndexLoaded?: (flatIndex: number) => boolean
}

export interface ScrubberPill {
  label: string
  topFraction: number
}

export interface RailHandlers {
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void
  onPointerMove: (e: React.PointerEvent<HTMLElement>) => void
  onPointerUp: (e: React.PointerEvent<HTMLElement>) => void
  onPointerLeave: (e: React.PointerEvent<HTMLElement>) => void
  onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void
}

export interface UseDateScrubberResult {
  railRef: React.MutableRefObject<HTMLDivElement | null>
  visible: boolean
  yearMarks: Array<YearMark>
  pill: ScrubberPill | null
  indicatorFraction: number
  isDragging: boolean
  railHandlers: RailHandlers
}

export const monthLabel = (month: FlatMonth, language: string): string => {
  const label = new Intl.DateTimeFormat(language, { month: 'long', year: 'numeric' }).format(
    month.date
  )
  return label.charAt(0).toUpperCase() + label.slice(1)
}

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value))

export const useDateScrubber = ({
  timeline,
  fraction,
  onJump,
  isIndexLoaded,
}: UseDateScrubberOptions): UseDateScrubberResult => {
  const railRef = React.useRef<HTMLDivElement | null>(null)
  const [railHeight, setRailHeight] = React.useState(0)
  const [viewportHeight, setViewportHeight] = React.useState(
    typeof window === 'undefined' ? 0 : window.innerHeight
  )
  // desktop-only (md: breakpoint) — on touch viewports the rail fights with scrolling
  const [isDesktop, setIsDesktop] = React.useState(
    typeof window === 'undefined' ? false : window.innerWidth >= 768
  )
  const [isDragging, setIsDragging] = React.useState(false)
  const [dragFraction, setDragFraction] = React.useState(0)
  const [pill, setPill] = React.useState<ScrubberPill | null>(null)
  const lastJumpedMonthRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    const measure = () => {
      setRailHeight(railRef.current?.getBoundingClientRect().height ?? 0)
      setViewportHeight(window.innerHeight)
      setIsDesktop(window.innerWidth >= 768)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  const visible = isDesktop && shouldShowScrubber(timeline.total, viewportHeight)

  // The scrubber replaces the native scrollbar (OneDrive-style): hide it while shown
  React.useEffect(() => {
    if (!visible) return
    document.documentElement.classList.add('native-scrollbar-hidden')
    return () => document.documentElement.classList.remove('native-scrollbar-hidden')
  }, [visible])

  const yearMarks = React.useMemo(() => computeYearMarks(timeline, railHeight), [
    timeline,
    railHeight,
  ])

  const fractionFromEvent = (e: React.PointerEvent<HTMLElement>): number => {
    const rect = railRef.current?.getBoundingClientRect()
    if (!rect || rect.height === 0) return 0
    return clamp01((e.clientY - rect.top) / rect.height)
  }

  const pillFor = (railFraction: number): ScrubberPill | null => {
    const month = timeline.fractionToMonth(railFraction)
    if (!month) return null
    return { label: monthLabel(month, i18next.language), topFraction: railFraction }
  }

  const jumpTo = (railFraction: number, { liveOnly }: { liveOnly: boolean }) => {
    const month = timeline.fractionToMonth(railFraction)
    if (!month || lastJumpedMonthRef.current === month.key) return
    if (liveOnly && !isIndexLoaded?.(month.itemsBefore)) return
    lastJumpedMonthRef.current = month.key
    onJump(month)
  }

  const railHandlers: RailHandlers = {
    onPointerDown: (e) => {
      e.preventDefault()
      railRef.current?.setPointerCapture(e.pointerId)
      lastJumpedMonthRef.current = null
      const railFraction = fractionFromEvent(e)
      setIsDragging(true)
      setDragFraction(railFraction)
      setPill(pillFor(railFraction))
      jumpTo(railFraction, { liveOnly: true })
    },
    onPointerMove: (e) => {
      const railFraction = fractionFromEvent(e)
      if (isDragging) {
        setDragFraction(railFraction)
        setPill(pillFor(railFraction))
        jumpTo(railFraction, { liveOnly: true })
      } else if (e.pointerType === 'mouse') {
        setPill(pillFor(railFraction))
      }
    },
    onPointerUp: (e) => {
      if (!isDragging) return
      const railFraction = fractionFromEvent(e)
      setIsDragging(false)
      setPill(e.pointerType === 'mouse' ? pillFor(railFraction) : null)
      jumpTo(railFraction, { liveOnly: false })
    },
    onPointerLeave: () => {
      if (!isDragging) setPill(null)
    },
    onPointerCancel: () => {
      setIsDragging(false)
      setPill(null)
    },
  }

  return {
    railRef,
    visible,
    yearMarks,
    pill,
    indicatorFraction: isDragging ? dragFraction : clamp01(fraction),
    isDragging,
    railHandlers,
  }
}
